import { createClient } from '@supabase/supabase-js'
import type { SupabaseClient } from '@supabase/supabase-js'

/**
 * The Supabase connection
 * -------------------------------------------------------------
 * One client for the whole app, built the first time something asks for it.
 *
 * Only the anon key ever reaches this file. That key is designed to be public:
 * it grants nothing on its own, and every table is guarded by Row Level
 * Security (see docs/schema.sql). The service_role key, which bypasses RLS,
 * must never appear in anything the browser downloads — it belongs in the
 * Cloudflare Worker's secrets.
 */

const URL_KEY = 'VITE_SUPABASE_URL'
const ANON_KEY = 'VITE_SUPABASE_ANON_KEY'

/** Bucket holding the library images. */
export const STORAGE_BUCKET = import.meta.env.VITE_SUPABASE_BUCKET || 'assets'

let client: SupabaseClient | null = null
let warned = false

/** True when both settings are present, so callers can fall back quietly. */
export function isSupabaseConfigured(): boolean {
  return Boolean(import.meta.env[URL_KEY] && import.meta.env[ANON_KEY])
}

/**
 * The shared client, or null when the project is not configured. Returning
 * null rather than throwing lets the app keep running on mock data instead of
 * showing a blank page to someone who simply has no .env.local yet.
 */
export function getSupabase(): SupabaseClient | null {
  if (client) return client
  if (!isSupabaseConfigured()) {
    if (!warned) {
      warned = true
      console.warn(
        `[WonderWorks] ${URL_KEY} and ${ANON_KEY} are not set. Copy .env.example to .env.local and fill them in.`,
      )
    }
    return null
  }

  client = createClient(
    import.meta.env[URL_KEY] as string,
    import.meta.env[ANON_KEY] as string,
    {
      auth: {
        // Sign-in is Microsoft Entra ID through this client (see authService).
        //
        // persistSession keeps the session in localStorage, so a colleague who
        // closes the tab comes back signed in; autoRefreshToken spends the
        // refresh token that `offline_access` earned us, which is what makes
        // "signed in until you sign out" true rather than "signed in for an
        // hour"; detectSessionInUrl is what completes the round trip from
        // Microsoft, reading the code off /auth/callback and exchanging it.
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        // Pinned rather than left to the library's default: PKCE returns a
        // short-lived code in the query string instead of putting tokens in
        // the URL fragment, where they reach browser history and any script on
        // the page.
        flowType: 'pkce',
      },
    },
  )
  return client
}

/**
 * There used to be a `storageUrl()` here, turning a stored path into a public
 * URL. It is gone, and deliberately not replaced by anything with that shape.
 *
 * The bucket is private now, so no function can turn a path into a working
 * link on its own — a link has to be signed, and signing is a request to
 * Supabase. That happens once per page of results in the repositories rather
 * than once per image, so a helper like the old one would be a trap: it would
 * look right, compile, and quietly produce URLs that fail.
 *
 * See services/signedUrls.ts.
 */
